import React, { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '../providers/ThemeProvider';
import { ChartDataService } from '../services/ChartDataService';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { useSAPDataStore } from '../store/SAPDataStore';

interface PresetChangeProps {
  machineId?: string;
  machineName?: string;
  machineData?: any;
}

interface PresetChange {
  id?: number;
  shift: string;
  preset: string;
  old_value: string;
  new_value: string;
  timestamp?: string;
}

// Response format: { results: [{ timestamp, preset, old_value, new_value, shift }, ...] }
interface PresetDetailResponse {
  status: string;
  results: PresetChange[];
}

interface ChartData {
  data: any[];
  presetNames: string[];
  colors: { [key: string]: string };
}

const PresetChange: React.FC<PresetChangeProps> = ({ machineId, machineName, machineData }) => {
  const { currentTheme } = useTheme();
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [presetData, setPresetData] = useState<PresetDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch preset data via NATS
  const fetchPresetData = async (serialno: string, dateFrom: string, dateTo: string) => {
    if (!token) {
      setError('Authentication required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const params = ChartDataService.buildRequestParams(serialno, { start: dateFrom, end: dateTo }, []);
      const response = await ChartDataService.fetchPresetDetails(params, token);
      
      if (response.success && response.data) {
        const responseData = response.data as any;
        // Backend returns { results: [{ timestamp, preset, old_value, new_value, shift }, ...] }
        const results = Array.isArray(responseData) ? responseData : (responseData.results || []);
        
        setPresetData({ status: 'success', results: results } as PresetDetailResponse);
      } else {
        setError('Failed to fetch preset data');
      }
    } catch (err) {
      console.error('Error fetching preset data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or dependencies change
  useEffect(() => {
    if (machineId && dateRange.start && dateRange.end) {
      // Use machineId as serial number - adjust if needed based on your data structure
      const serialno = machineData?.serialNumber || machineId;
      fetchPresetData(serialno, dateRange.start, dateRange.end);
    }
  }, [machineId, machineData, dateRange.start, dateRange.end]);

  // Transform preset data for Highcharts timeline
  const getChartData = (): ChartData => {
    if (!presetData?.results || !Array.isArray(presetData.results) || presetData.results.length === 0) {
      return { data: [], presetNames: [], colors: {} };
    }

    const timelineData: any[] = [];
    const presetColors: { [key: string]: string } = {};
    const colorPalette = [
      '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed',
      '#db2777', '#0891b2', '#65a30d', '#c2410c', '#9333ea'
    ];
    let colorIndex = 0;

    // Process flat array: [{ timestamp, preset, old_value, new_value, shift }, ...]
    presetData.results.forEach((change) => {
      // Assign color to preset if not already assigned
      if (!presetColors[change.preset]) {
        presetColors[change.preset] = colorPalette[colorIndex % colorPalette.length];
        colorIndex++;
      }

      const startTime = new Date(change.timestamp || '').getTime();
      
      timelineData.push({
        x: startTime,
        y: Object.keys(presetColors).indexOf(change.preset), // Y position based on preset type
        name: change.preset,
        shift: change.shift,
        oldValue: change.old_value,
        newValue: change.new_value,
        color: presetColors[change.preset],
        marker: {
          radius: 6
        }
      });
    });

    return {
      data: timelineData,
      presetNames: Object.keys(presetColors),
      colors: presetColors
    };
  };

  const chartData = getChartData();

  // Highcharts configuration
  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'scatter',
      backgroundColor: 'transparent',
      height: 400,
      zooming: {
        type: 'x'
      }
    },
    title: {
      text: `Preset Changes Timeline - ${machineName}`,
      style: {
        color: currentTheme.colors.text.primary,
        fontSize: '18px',
        fontWeight: 'bold'
      }
    },
    subtitle: {
      text: 'Click and drag to zoom in on time periods',
      style: {
        color: currentTheme.colors.text.secondary,
        fontSize: '12px'
      }
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Time',
        style: {
          color: currentTheme.colors.text.primary
        }
      },
      labels: {
        style: {
          color: currentTheme.colors.text.secondary
        }
      },
      gridLineColor: currentTheme.colors.border.primary,
      lineColor: currentTheme.colors.border.primary
    },
    yAxis: {
      title: {
        text: 'Preset Type',
        style: {
          color: currentTheme.colors.text.primary
        }
      },
      categories: chartData.presetNames,
      labels: {
        style: {
          color: currentTheme.colors.text.secondary
        }
      },
      gridLineColor: currentTheme.colors.border.primary,
      lineColor: currentTheme.colors.border.primary
    },
    legend: {
      enabled: false // We'll show preset types on Y-axis instead
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 6,
          states: {
            hover: {
              enabled: true,
              lineColor: 'rgb(100,100,100)'
            }
          }
        },
        states: {
          hover: {
            enabled: true
          }
        }
      }
    },
    tooltip: {
      backgroundColor: currentTheme.colors.background.secondary,
      borderColor: currentTheme.colors.border.primary,
      style: {
        color: currentTheme.colors.text.primary
      },
      formatter: function() {
        const point = this as any;
        return `
          <div style="padding: 8px;">
            <strong>${point.point.name}</strong><br/>
            <strong>Time:</strong> ${Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', point.x!)}<br/>
            <strong>Shift:</strong> ${point.point.shift}<br/>
            <strong>Old Value:</strong> ${point.point.oldValue}<br/>
            <strong>New Value:</strong> ${point.point.newValue}
          </div>
        `;
      },
      useHTML: true
    },
    series: [{
      type: 'scatter',
      name: 'Preset Changes',
      data: chartData.data,
      showInLegend: false
    }],
    credits: {
      enabled: false
    }
  };

  return (
    <>
      {/* Chart Container */}
      <div className="rounded-lg border p-4" 
           style={{ 
             backgroundColor: 'var(--bg-primary)', 
             borderColor: 'var(--border-primary)' 
           }}>
        {loading && (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading preset data...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <p className="text-red-500 mb-2">Error loading preset data</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{error}</p>
              <button
                onClick={() => {
                  if (machineId && dateRange.start && dateRange.end) {
                    const serialno = machineData?.serialNumber || machineId;
                    fetchPresetData(serialno, dateRange.start, dateRange.end);
                  }
                }}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {!loading && !error && chartData.data.length === 0 && (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <p style={{ color: 'var(--text-secondary)' }}>No preset changes found for the selected time period</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Try adjusting the date range or check if the machine has any preset data
              </p>
            </div>
          </div>
        )}

        {!loading && !error && chartData.data.length > 0 && (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            ref={chartRef}
          />
        )}
      </div>

      {/* Data Summary */}
      {!loading && !error && presetData?.results && Array.isArray(presetData.results) && presetData.results.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg border text-center"
               style={{ 
                 backgroundColor: 'var(--bg-primary)', 
                 borderColor: 'var(--border-primary)' 
               }}>
            <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {chartData.data.length}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Total Changes
            </div>
          </div>
          <div className="p-4 rounded-lg border text-center"
               style={{ 
                 backgroundColor: 'var(--bg-primary)', 
                 borderColor: 'var(--border-primary)' 
               }}>
            <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {chartData.presetNames.length}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Preset Types
            </div>
          </div>
          <div className="p-4 rounded-lg border text-center"
               style={{ 
                 backgroundColor: 'var(--bg-primary)', 
                 borderColor: 'var(--border-primary)' 
               }}>
            <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {presetData.results.length}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Time Points
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PresetChange;
